import axios from 'axios';
import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, Snackbar, Alert } from '@mui/material';
import ModuleLayout from './ModuleLayout';
import Timeline, { AssessmentTimelineStep } from './Timeline';
import ActionsSidebar from './ActionsSidebar';
import { courseAPI, notesAPI, progressAPI } from '../services/api';
import '../styles/components/_module-page-layout.scss';
import { getLocalizedText, getDisplayLocale } from '../utils/i18n';
import CourseLanguageDialog from './CourseLanguageDialog';
import { getCourseAvailableLanguages, isCourseLanguageAvailable } from '../utils/courseLanguages';
import { getLearnerFacingApiError } from '../utils/learnerErrors';
import { normalizeId } from '../utils/id';
import { arePriorChaptersCompleted } from '../utils/chapterAccess';
import { exportCourseNotesDocx } from '../utils/exportCourseNotesDocx';

interface ProgressContextType {
  refreshProgress: () => Promise<void>;
  refreshAllProgress: () => Promise<void>;
  refreshCourse: () => Promise<void>;
  /** Formation avec diagnostic obligatoire non soumis : pas d'accès aux modules tant que ce n'est pas fait. */
  diagnosticBlocking: boolean;
}

export const ProgressContext = createContext<ProgressContextType | null>(null);

export const useProgressContext = () => {
  const context = useContext(ProgressContext);
  if (!context) {
    throw new Error('useProgressContext must be used within ModulePageLayout');
  }
  return context;
};

interface ModulePageLayoutProps {
  children: React.ReactNode;
  loading?: boolean;
  error?: string | null;
}

/**
 * Layout commun pour toutes les pages de modules (Quiz, Capsule, Workshop, etc.)
 * Gère le chargement du cours, la Timeline et la navigation
 */
const ModulePageLayout: React.FC<ModulePageLayoutProps> = ({ 
  children, 
  loading = false,
  error = null 
}) => {
  const { courseId, chapterId, quizId, capsuleId, workshopId, quizOrder, capsuleOrder, workshopOrder, measurement, followupId } = useParams<{ 
    courseId: string; 
    chapterId?: string;
    quizId?: string;
    capsuleId?: string;
    workshopId?: string;
    quizOrder?: string;
    capsuleOrder?: string;
    workshopOrder?: string;
    measurement?: 'initial' | 'final' | 'followup';
    followupId?: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const [course, setCourse] = useState<any>(null);
  const [courseLoading, setCourseLoading] = useState(true);
  const [courseError, setCourseError] = useState<string | null>(null);
  const [chapterProgress, setChapterProgress] = useState<any>(null);
  const [allChaptersProgress, setAllChaptersProgress] = useState<{ [chapterId: string]: any }>({});
  const [languageDialogOpen, setLanguageDialogOpen] = useState(false);
  const [availableLanguages, setAvailableLanguages] = useState<string[]>([]);
  const [notesEmptySnackbarOpen, setNotesEmptySnackbarOpen] = useState(false);
  const [downloadNotesLoading, setDownloadNotesLoading] = useState(false);

  useEffect(() => {
    if (courseId) {
      loadCourse();
    }
  }, [courseId]); // Only reload course when courseId changes, not when navigating between items

  useEffect(() => {
    if (!course) return;
    const currentLang = i18n.language.split('-')[0] || 'fr';
    const courseLanguages = getCourseAvailableLanguages(course);
    setAvailableLanguages(courseLanguages);
    setLanguageDialogOpen(!isCourseLanguageAvailable(course, currentLang) && courseLanguages.length > 0);
  }, [course, i18n.language]);

  useEffect(() => {
    if (chapterId) {
      loadChapterProgress();
    }
  }, [chapterId]); // Load progress when chapter changes

  useEffect(() => {
    if (course && course.chapters) {
      loadAllChaptersProgress();
    }
  }, [course]); // Load progress for all chapters when course is loaded

  const loadCourse = async (showLoading: boolean = true) => {
    if (!courseId) {
      setCourseError(t('common.missingParameters'));
      setCourseLoading(false);
      return;
    }

    try {
      if (showLoading) {
        setCourseLoading(true);
      }
      setCourseError(null);
      
      const courseData = await courseAPI.getCourse(courseId);
      setCourse(courseData);
      const currentLang = i18n.language.split('-')[0] || 'fr';
      const courseLanguages = getCourseAvailableLanguages(courseData);
      setAvailableLanguages(courseLanguages);
      setLanguageDialogOpen(!isCourseLanguageAvailable(courseData, currentLang) && courseLanguages.length > 0);
    } catch (err: unknown) {
      console.error('Error loading course:', err);

      if (axios.isAxiosError(err) && err.response?.status === 401) {
        localStorage.removeItem('student_token');
        localStorage.removeItem('student_user');
        navigate('/login');
        return;
      }

      if (axios.isAxiosError(err) && err.response?.status === 403) {
        setCourseError(getLearnerFacingApiError(err, t));
        return;
      }

      setCourseError(getLearnerFacingApiError(err, t));
    } finally {
      if (showLoading) {
        setCourseLoading(false);
      }
    }
  };

  const loadChapterProgress = async () => {
    if (!chapterId) return;

    try {
      const progressData = await progressAPI.getChapterProgress(chapterId);
      setChapterProgress(progressData.progress);
      // Also update in allChaptersProgress
      setAllChaptersProgress((prev) => ({
        ...prev,
        [chapterId]: progressData.progress
      }));
    } catch (err: any) {
      // If no progress found, that's fine - set to null
      console.log('No progress found for chapter:', chapterId);
      setChapterProgress(null);
      setAllChaptersProgress((prev) => ({
        ...prev,
        [chapterId]: null
      }));
    }
  };

  const loadAllChaptersProgress = async () => {
    if (!courseId || !course?.chapters) return;

    try {
      // Single request for the whole course (avoids N× GET /progress/chapter/:id)
      const { byChapter } = await progressAPI.getCourseProgress(courseId);
      const progressMap: { [chapterId: string]: any } = {};

      for (const chapter of course.chapters) {
        const id = String(chapter._id);
        progressMap[id] = byChapter?.[id] ?? null;
      }

      setAllChaptersProgress(progressMap);

      if (chapterId) {
        setChapterProgress(progressMap[String(chapterId)] ?? null);
      }
    } catch (err: any) {
      console.error('Error loading chapters progress:', err);
    }
  };

  const handleSelectCourseLanguage = async (lang: string) => {
    await i18n.changeLanguage(lang);
    setLanguageDialogOpen(false);
  };

  const handleDownloadCourseNotes = useCallback(async () => {
    if (!courseId || !course) return;
    setDownloadNotesLoading(true);
    try {
      const notes = await notesAPI.getCourseNotes(courseId);
      let firstName = '';
      let lastName = '';
      try {
        const raw = localStorage.getItem('student_user');
        if (raw) {
          const u = JSON.parse(raw) as { firstName?: string; lastName?: string };
          if (typeof u.firstName === 'string') firstName = u.firstName;
          if (typeof u.lastName === 'string') lastName = u.lastName;
        }
      } catch {
        /* ignore */
      }
      const result = await exportCourseNotesDocx({
        course,
        notes,
        firstName,
        lastName,
        t,
      });
      if (!result.ok && result.reason === 'empty') {
        setNotesEmptySnackbarOpen(true);
      }
    } catch (err) {
      console.error('Error exporting course notes:', err);
    } finally {
      setDownloadNotesLoading(false);
    }
  }, [courseId, course, t]);

  /** Détecte si l'apprenant est actuellement sur la page assessment (initial/final/followup). */
  const getActiveAssessmentMeasurement = (): 'initial' | 'final' | 'followup' | null => {
    if (measurement) return measurement;
    if (location.pathname.includes('/assessment/initial')) return 'initial';
    if (location.pathname.includes('/assessment/final')) return 'final';
    if (location.pathname.includes('/assessment/followup')) return 'followup';
    return null;
  };

  /** Construit les étapes diagnostic, mesure finale et suivis M+ à partir du payload `course.assessment`. */
  const buildAssessmentSteps = (): {
    diagnostic: AssessmentTimelineStep | null;
    final: AssessmentTimelineStep | null;
    followups: AssessmentTimelineStep[];
  } => {
    const a = course?.assessment;
    if (!a?.enabled) return { diagnostic: null, final: null, followups: [] };

    const activeMeasurement = getActiveAssessmentMeasurement();
    const activeFollowupId =
      activeMeasurement === 'followup' && followupId ? normalizeId(followupId) : null;

    const diagnostic: AssessmentTimelineStep = {
      kind: 'initial',
      title: t('assessment.diagnostic.title'),
      isCompleted: !!a.initial?.submitted,
      isLocked: false,
      isActive: activeMeasurement === 'initial',
    };

    let finalLockedHint: string | undefined;
    const reason = a.eligibility?.finalImpactBlockedReason;
    if (reason === 'workshops' && a.eligibility?.finalImpactExpectedAfter) {
      const lang = i18n.language.split('-')[0] || 'fr';
      const locale = getDisplayLocale(lang);
      const formatted = new Date(a.eligibility.finalImpactExpectedAfter).toLocaleDateString(
        locale,
        { day: '2-digit', month: '2-digit' }
      );
      finalLockedHint = t('assessment.final.lockedWorkshops', { date: formatted });
    } else if (reason === 'chapters') {
      finalLockedHint = t('assessment.final.lockedChapters');
    } else if (reason === 'diagnostic') {
      finalLockedHint = t('assessment.final.lockedDiagnostic');
    }

    const rule = a.eligibility?.finalImpactUnlockRule;
    let unlockConditionHint: string | undefined;
    if (!a.final?.submitted && rule && a.initial?.submitted) {
      unlockConditionHint =
        rule === 'after-last-workshop'
          ? t('assessment.final.unlockHintWorkshop')
          : t('assessment.final.unlockHintCourseEnd');
    }

    const final: AssessmentTimelineStep = {
      kind: 'final',
      title: t('assessment.final.title'),
      isCompleted: !!a.final?.submitted,
      // Ne pas utiliser activeMeasurement ici : sinon l'étape paraît débloquée sur certaines routes.
      isLocked: !a.final?.submitted && !a.eligibility?.finalImpactAvailable,
      isActive: activeMeasurement === 'final',
      lockedHint: finalLockedHint,
      unlockConditionHint,
    };

    // Suivis M+ : n'afficher dans la timeline que les mesures déjà faites ou ouvrables maintenant
    // (pas celles dont la date de publication est encore dans le futur).
    const followups: AssessmentTimelineStep[] = [...(a.followups || [])]
      .filter((fu) => fu.submitted || fu.available || fu.pendingDiagnostic)
      .sort((x, y) => (x.order ?? 0) - (y.order ?? 0))
      .map((fu) => {
        const fid = normalizeId(fu.id) || String(fu.id);
        const isCompleted = !!fu.submitted;
        const isLocked = !isCompleted && !fu.available;
        let lockedHint: string | undefined;
        if (isLocked && fu.pendingDiagnostic) {
          lockedHint = t('assessment.final.lockedDiagnostic');
        } else if (isLocked && fu.unlockDate) {
          const lang = i18n.language.split('-')[0] || 'fr';
          const locale = getDisplayLocale(lang);
          const formatted = new Date(fu.unlockDate).toLocaleDateString(locale, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          });
          lockedHint = t('assessment.followup.scheduledFor', { date: formatted });
        }
        return {
          kind: 'followup' as const,
          followupId: fid,
          title: getLocalizedText(fu.label, t('assessment.followup.label')),
          isCompleted,
          isLocked,
          isActive: activeFollowupId === fid,
          lockedHint,
        };
      });

    return { diagnostic, final, followups };
  };

  const handleAssessmentClick = (kind: 'initial' | 'final' | 'followup', fuId?: string) => {
    if (!courseId || !course?.assessment) return;
    const a = course.assessment;
    if (kind === 'followup') {
      if (!fuId) return;
      const fu = (a.followups || []).find((f: { id: string }) => normalizeId(f.id) === normalizeId(fuId));
      if (!fu || fu.submitted) return;
      if (fu.pendingDiagnostic) {
        navigate(`/course/${courseId}/assessment/initial`);
        return;
      }
      if (!fu.available) return;
      navigate(`/course/${courseId}/assessment/followup/${normalizeId(fuId)}`);
      return;
    }
    if (kind === 'final') {
      if (
        !a.final?.submitted &&
        !a.initial?.submitted &&
        a.eligibility?.finalImpactBlockedReason === 'diagnostic'
      ) {
        navigate(`/course/${courseId}/assessment/initial`);
        return;
      }
    }
    if (kind === 'final' && !a.final?.submitted && !a.eligibility?.finalImpactAvailable) {
      return;
    }
    navigate(`/course/${courseId}/assessment/${kind}`);
  };

  /** Aligné sur CourseDetail : tant que le diagnostic obligatoire n'est pas soumis, rien d'autre n'est accessible. */
  const isDiagnosticBlocking = (): boolean => {
    const a = course?.assessment;
    if (!a?.enabled) return false;
    return Boolean(a.eligibility?.diagnosticRequired && !a.eligibility?.diagnosticSubmitted);
  };

  const buildTimelineData = () => {
    if (!course) return { courseTitle: '', progress: 0, chapters: [] };

    const diagnosticBlocking = isDiagnosticBlocking();

    // Current item id from URL (new routes), with fallback to legacy order params
    const currentItemId = quizId || capsuleId || workshopId;
    const currentItemOrder = quizOrder || capsuleOrder || workshopOrder;

    let totalCompletedItems = 0;
    let totalItems = 0;

    // Trouver le premier item non complété pour le délocker (sauf si le diagnostic bloque tout le parcours)
    let firstUncompletedItemFound = false;
    let firstUncompletedItemId: string | null = null;

    if (!diagnosticBlocking) {
      // D'abord, parcourir tous les chapitres et items pour trouver le premier non complété
      const sortedChapters = [...(course.chapters || [])].sort((a: any, b: any) => a.order - b.order);
      for (const chapter of sortedChapters) {
        const isChapterLockedByDate = chapter.unlockDate
          ? new Date(chapter.unlockDate) > new Date()
          : false;
        if (isChapterLockedByDate) continue;
        if (!arePriorChaptersCompleted(sortedChapters, chapter._id)) continue;

        const sortedItems = [...(chapter.items || [])].sort((a: any, b: any) => a.order - b.order);
        for (const item of sortedItems) {
          const isCompleted = item.isCompleted || false;
          if (!isCompleted && !firstUncompletedItemFound) {
            firstUncompletedItemFound = true;
            firstUncompletedItemId = normalizeId(item?._id || item?.id) || `${normalizeId(chapter._id)}-${item.order}`;
            break;
          }
        }
        if (firstUncompletedItemFound) break;
      }
    }

    // Trier les chapitres par order pour garantir l'ordre correct dans la timeline
    const sortedChaptersForMapping = [...(course.chapters || [])].sort((a: any, b: any) => a.order - b.order);

    const chapters = sortedChaptersForMapping.map((chapter: any) => {
      // Verrouillage chapitre : date, flag backend, diagnostic, ou chapitres précédents incomplets
      const dateChapterLocked = chapter.unlockDate ? new Date(chapter.unlockDate) > new Date() : false;
      const priorChaptersComplete = arePriorChaptersCompleted(
        sortedChaptersForMapping,
        chapter._id
      );
      const isChapterLocked =
        diagnosticBlocking ||
        dateChapterLocked ||
        Boolean(chapter.isLocked) ||
        !priorChaptersComplete;

      // Trier les items par order pour garantir l'ordre correct
      const sortedItems = [...(chapter.items || [])].sort((a: any, b: any) => a.order - b.order);

      const items = sortedItems.map((item: any) => {
        const itemId = normalizeId(item?._id || item?.id) || `${normalizeId(chapter._id)}-${item.order}`;
        const isActive = Boolean(
          chapter._id === chapterId && 
          (
            (currentItemId && itemId === currentItemId) ||
            (
              currentItemOrder &&
              item.order.toString() === currentItemOrder &&
              ((item.itemType === 'quiz' && quizOrder) || 
               (item.itemType === 'capsule' && capsuleOrder) ||
               (item.itemType === 'workshop' && workshopOrder))
            )
          )
        );
        
        // Use isCompleted directly from backend data (already calculated correctly)
        // This ensures consistency with CourseDetail page which uses course.progress
        const isCompleted = item.isCompleted || false;

        // Déterminer si l'item est locké :
        // - Diagnostic obligatoire non fait → tout est bloqué (via isChapterLocked)
        // - Si le chapitre est locké, tous les items sont lockés
        // - Sinon, seul le premier item non complété (trouvé précédemment) est délocké
        let isItemLocked = false;
        if (diagnosticBlocking || isChapterLocked) {
          isItemLocked = true;
        } else if (!isCompleted) {
          // Si c'est le premier item non complété trouvé, il est délocké
          if (itemId === firstUncompletedItemId) {
            isItemLocked = false;
          } else {
            // Sinon, il est locké
            isItemLocked = true;
          }
        } else {
          // Les items complétés ne sont jamais lockés
          isItemLocked = false;
        }

        if (isCompleted) totalCompletedItems++;
        totalItems++;

        return {
          id: itemId,
          type: item.itemType,
          title: getLocalizedText(item.title),
          description: item.description ? getLocalizedText(item.description) : undefined,
          duration: item.duration,
          date: item.date,
          startTime: item.startTime,
          endTime: item.endTime,
          isCompleted,
          isActive,
          isLocked: isItemLocked
        };
      }) || [];

      const completedCount = items.filter((item: any) => item.isCompleted).length;
      const totalCount = items.length;

      return {
        id: chapter._id,
        title: getLocalizedText(chapter.title),
        items,
        completedCount,
        totalCount,
        isLocked: isChapterLocked,
        unlockDate: chapter.unlockDate,
        deadline: chapter.deadline,
        remainingCount: totalCount - completedCount
      };
    }) || [];

    // Calculate overall course progress
    // Use the same calculation as backend for consistency
    let overallProgress = totalItems > 0 ? Math.round((totalCompletedItems / totalItems) * 100) : 0;

    // Plafond 99 % tant que la mesure d'impact finale n'est pas soumise (cohérent avec le backend).
    const a = course.assessment;
    if (a?.enabled && !a.final?.submitted && overallProgress >= 100) {
      overallProgress = 99;
    }

    return {
      courseTitle: getLocalizedText(course.title),
      progress: overallProgress,
      chapters
    };
  };

  const handleItemClick = (itemId: string, chapterId: string) => {
    if (!courseId || !course) return;
    if (isDiagnosticBlocking()) return;

    const chId = chapterId;
    const item = course.chapters
      ?.find((ch: any) => ch._id === chId)
      ?.items?.find((it: any) => normalizeId(it?._id || it?.id) === itemId);

    if (!item) return;
    
    if (item.itemType === 'capsule') {
      navigate(`/course/${courseId}/chapter/${normalizeId(chId)}/capsule/${itemId}`);
    } else if (item.itemType === 'quiz') {
      navigate(`/course/${courseId}/chapter/${normalizeId(chId)}/quiz/${itemId}`);
    } else if (item.itemType === 'workshop') {
      navigate(`/course/${courseId}/chapter/${normalizeId(chId)}/workshop/${itemId}`);
    }
  };

  // Show loading state only for initial course load
  // Content loading should be handled by children
  if (courseLoading) {
    return (
      <Box className="module-page-layout__loading">
        <CircularProgress color="primary" />
      </Box>
    );
  }

  // Show error state
  if (courseError || error) {
    return (
      <Box className="module-page-layout__error">
        <Typography variant="h5" className="module-page-layout__error-title">
          {courseError || error || t('common.unexpectedError')}
        </Typography>
        <Button
          variant="contained"
          className="module-page-layout__error-button"
          onClick={() => courseId && navigate(`/course/${courseId}`)}
        >
          {t('modulePageLayout.backToCourse')}
        </Button>
      </Box>
    );
  }

  const timelineData = buildTimelineData();

  // Expose refresh functions via context
  // refreshCourse should not show loading screen (silent refresh)
  const progressContextValue: ProgressContextType = {
    refreshProgress: loadChapterProgress,
    refreshAllProgress: loadAllChaptersProgress,
    refreshCourse: () => loadCourse(false), // Silent refresh without loading screen
    diagnosticBlocking: isDiagnosticBlocking(),
  };

  const assessmentSteps = buildAssessmentSteps();

  return (
    <ProgressContext.Provider value={progressContextValue}>
      <>
        <ModuleLayout
          timeline={
            <Timeline
              courseTitle={timelineData.courseTitle}
              progress={timelineData.progress}
              chapters={timelineData.chapters}
              onItemClick={handleItemClick}
              assessmentDiagnostic={assessmentSteps.diagnostic}
              assessmentFinal={assessmentSteps.final}
              assessmentFollowups={assessmentSteps.followups}
              onAssessmentClick={handleAssessmentClick}
            />
          }
          content={children}
          actions={
            <ActionsSidebar
              courseId={courseId || ''}
              chapterId={chapterId}
              onBack={() => courseId && navigate(`/course/${courseId}`)}
              onDownloadCourseNotes={handleDownloadCourseNotes}
              downloadNotesLoading={downloadNotesLoading}
            />
          }
        />
        <CourseLanguageDialog
          open={languageDialogOpen}
          availableLanguages={availableLanguages}
          onSelectLanguage={handleSelectCourseLanguage}
        />
        <Snackbar
          open={notesEmptySnackbarOpen}
          autoHideDuration={5000}
          onClose={() => setNotesEmptySnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setNotesEmptySnackbarOpen(false)}
            severity="info"
            variant="filled"
            sx={{ width: '100%' }}
          >
            {t('notesExport.noNotesToDownload')}
          </Alert>
        </Snackbar>
      </>
    </ProgressContext.Provider>
  );
};

export default ModulePageLayout;

